import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Instagram, Music, Youtube, Facebook, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import AvatarUploader from "@/components/AvatarUploader";
import CitySelector from "@/components/CitySelector";
import HandleInput from "@/components/HandleInput";
import AlbumCard from "@/components/AlbumCard";
import { ALBUMS, albumLabel } from "@/config/albumCatalog";
import { PLATFORMS, normalizeHandle, extractHandleFromLegacy } from "@/lib/socialHandles";

const USERNAME_RE = /^[A-Za-z0-9_-]{3,32}$/;
const BIO_MAX = 500;
const DEBOUNCE_MS = 400;

const SOCIAL_FIELDS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook",  label: "Facebook",  Icon: Facebook  },
  { key: "tiktok",    label: "TikTok",    Icon: Music     },
  { key: "youtube",   label: "YouTube",   Icon: Youtube   },
];

const COUNTRY_OPTIONS = [
  { code: "", label: "— Select —" },
  { code: "US", label: "United States" }, { code: "GB", label: "United Kingdom" },
  { code: "LB", label: "Lebanon" },       { code: "AE", label: "United Arab Emirates" },
  { code: "SA", label: "Saudi Arabia" },  { code: "CA", label: "Canada" },
  { code: "AU", label: "Australia" },     { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },        { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },         { code: "BR", label: "Brazil" },
  { code: "AR", label: "Argentina" },     { code: "MX", label: "Mexico" },
  { code: "JP", label: "Japan" },         { code: "IN", label: "India" },
  { code: "EG", label: "Egypt" },         { code: "JO", label: "Jordan" },
  { code: "TR", label: "Turkey" },        { code: "NL", label: "Netherlands" },
  { code: "BE", label: "Belgium" },       { code: "CH", label: "Switzerland" },
  { code: "SE", label: "Sweden" },        { code: "PT", label: "Portugal" },
];

export default function ProfileTab() {
  const { collector, refresh } = useCollectorAuth();
  const [form, setForm] = useState(() => initFromCollector(collector));
  const [uname, setUname] = useState({ state: "idle", message: "" });
  const [busy, setBusy] = useState(false);
  const [initial, setInitial] = useState(() => initFromCollector(collector));
  const debounceRef = useRef(null);

  useEffect(() => {
    // Keep local form in sync if collector context refreshes (e.g. after avatar upload).
    // NARROW-TRACKED BY DESIGN: reseeding on every `collector` object change would
    // clobber the user's in-progress edits (display_name/bio/country/albums…). We
    // only reseed on identity + avatar/username signals, which are the fields that
    // change out-of-band from the form. This is intentional and NOT a stale-closure bug.
    const seed = initFromCollector(collector);
    setForm(seed);
    setInitial(seed);
  }, [collector?.id, collector?.avatar_media_id, collector?.username, collector?.avatar_updated_at]);

  // Unsaved-change warning — beforeunload only if form differs from initial.
  useEffect(() => {
    const dirty = JSON.stringify(form) !== JSON.stringify(initial);
    if (!dirty) return;
    const onBefore = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [form, initial]);

  // Live username availability check (skip if unchanged from current).
  useEffect(() => {
    const v = form.username.trim();
    if (!v || v.toLowerCase() === (collector?.username || "").toLowerCase()) {
      setUname({ state: "idle", message: "" });
      return;
    }
    if (!USERNAME_RE.test(v)) {
      setUname({ state: "invalid", message: "3–32 letters, numbers, _ or -" });
      return;
    }
    setUname({ state: "checking", message: "Checking…" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/collector-auth/username-check?u=${encodeURIComponent(v)}`);
        setUname(r.data?.available
          ? { state: "available", message: "Available." }
          : { state: "taken", message: r.data.message || "Not available." });
      } catch (_) { setUname({ state: "idle", message: "" }); }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form.username, collector?.username]);

  const bioLen = (form.bio || "").length;

  const setHandle = (k) => (bareHandle) =>
    setForm((f) => ({ ...f, social_handles: { ...f.social_handles, [k]: bareHandle } }));

  const setCity = (city) =>
    setForm((f) => ({ ...f, city }));

  const toggleAlbum = (slug, list) =>
    setForm((f) => {
      const isCompleted = list === "albums_completed";
      const already = (f[list] || []).includes(slug);
      let next = { ...f };
      if (already) {
        next[list] = f[list].filter((s) => s !== slug);
      } else {
        next[list] = [...(f[list] || []), slug];
        // Enforce mutual exclusion across the two lists
        const other = isCompleted ? "albums_in_progress" : "albums_completed";
        next[other] = (f[other] || []).filter((s) => s !== slug);
      }
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    if (uname.state === "taken" || uname.state === "invalid") {
      toast.error(uname.message || "Please pick a different username.");
      return;
    }
    setBusy(true);
    try {
      // Convert bare handles to full URLs for the backend (which validates URLs)
      const social_urls = {};
      for (const k of Object.keys(form.social_handles || {})) {
        const bare = (form.social_handles[k] || "").trim();
        if (!bare) continue;
        const r = normalizeHandle(k, bare);
        if (r.ok) social_urls[k] = r.fullUrl;
      }

      const payload = {
        display_name: form.display_name.trim(),
        bio: form.bio,
        country: form.country || null,
        timezone: form.city?.timezone || null,
        city: form.city || null,                      // NEW city block
        albums_completed: form.albums_completed || [],
        albums_in_progress: form.albums_in_progress || [],
        social_urls,
      };
      if (form.username && form.username.toLowerCase() !== (collector?.username || "").toLowerCase()) {
        payload.username = form.username.trim();
      }
      await api.patch("/me/profile", payload);
      toast.success("Profile saved.");
      await refresh();
      setInitial({ ...form });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const avatarUrl = collector?.avatar_media_id
    ? `${API}/me/profile/avatar?v=${collector.avatar_updated_at || collector.avatar_version || ""}`
    : null;

  return (
    <div className="space-y-10" data-testid="profile-tab">
      {/* -------- Avatar section -------- */}
      <Section title="Profile photo"
                subtitle="Square PNG or JPEG. We'll crop it to a circle wherever it appears.">
        <AvatarUploader
          currentUrl={avatarUrl}
          onUploaded={async () => { await refresh(); }}
          onRemoved={async () => { await refresh(); }}
          testidPrefix="profile-avatar"
        />
      </Section>

      <PrivacySection collector={collector} onSaved={refresh} />

      <form onSubmit={submit} className="space-y-10" data-testid="profile-form">
        <Section title="Public identity" subtitle="How you show up across the registry.">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Display name" value={form.display_name}
                    onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                    testid="profile-display-name" maxLength={80} />
            <UsernameField value={form.username}
                            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                            availability={uname}
                            testid="profile-username" />
          </div>
          <Textarea
            label={`Bio (${bioLen} / ${BIO_MAX})`}
            value={form.bio}
            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value.slice(0, BIO_MAX) }))}
            testid="profile-bio"
            placeholder="Tell fellow collectors what you're chasing, what you trade, and where you're based."
            rows={4}
          />
        </Section>

        <Section title="Location" subtitle="Type a city — we'll automatically set your time zone. Only your general location is shown publicly if privacy allows.">
          <div className="grid md:grid-cols-2 gap-5">
            <SelectField label="Country (optional)" value={form.country}
                          onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                          options={COUNTRY_OPTIONS.map((o) => ({ value: o.code, label: o.label }))}
                          testid="profile-country" />
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)] mb-1.5">City</div>
              <CitySelector
                value={form.city}
                onChange={setCity}
                testid="profile-city"
              />
            </div>
          </div>
        </Section>

        <Section title="Social handles"
                 subtitle="Type only your handle — the platform URL is added automatically. Leave blank to skip. Only enabled links appear on your public profile.">
          <div className="grid md:grid-cols-2 gap-5">
            {SOCIAL_FIELDS.map(({ key, label, Icon }) => (
              <div key={key}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon size={14} className="text-[var(--panini-red)]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">{label}</span>
                </div>
                <HandleInput
                  platform={key}
                  value={form.social_handles?.[key] || ""}
                  onChange={setHandle(key)}
                  testid={`profile-social-${key}`}
                />
              </div>
            ))}
          </div>
        </Section>

        <Section title="My World Cup albums"
                 subtitle="Pick every album you've completed and every album you're currently working on. Same catalog we use across the site.">
          <AlbumSelector
            selected={form.albums_completed || []}
            other={form.albums_in_progress || []}
            onToggle={(slug) => toggleAlbum(slug, "albums_completed")}
            title="Completed"
            testidPrefix="profile-album-completed"
            emptyLabel="No completed albums yet."
          />
          <div className="mt-6">
            <AlbumSelector
              selected={form.albums_in_progress || []}
              other={form.albums_completed || []}
              onToggle={(slug) => toggleAlbum(slug, "albums_in_progress")}
              title="In Progress"
              testidPrefix="profile-album-in-progress"
              emptyLabel="No in-progress albums yet."
            />
          </div>
        </Section>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--line)]">
          <button
            type="submit"
            disabled={busy}
            data-testid="profile-save"
            className="btn-red text-sm disabled:opacity-60"
          >
            <Save size={16} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function initFromCollector(c) {
  // Migrate legacy full-URL social values → bare handles for the new
  // HandleInput fields. Legacy values that can't be extracted are dropped from
  // the form UI but remain in the DB until the user re-saves (so we never
  // silently mutate stored data).
  const legacy = c?.social_urls || {};
  const handles = {};
  for (const key of Object.keys(PLATFORMS)) {
    handles[key] = extractHandleFromLegacy(key, legacy[key]);
  }
  return {
    display_name: c?.display_name || "",
    username: c?.username || "",
    bio: c?.bio || "",
    country: c?.country || "",
    city: c?.city || null,          // { city, state, country, timezone } | null
    albums_completed: Array.isArray(c?.albums_completed) ? c.albums_completed : [],
    albums_in_progress: Array.isArray(c?.albums_in_progress) ? c.albums_in_progress : [],
    social_handles: handles,
  };
}

function AlbumSelector({ selected, other, onToggle, title, testidPrefix, emptyLabel }) {
  return (
    <div data-testid={testidPrefix}>
      <div className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)] mb-2">
        {title} — {selected.length} selected
      </div>
      {selected.length === 0 && (
        <div className="text-sm text-[var(--smoke)] italic mb-3">{emptyLabel}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ALBUMS.map((a) => {
          const isSel = selected.includes(a.slug);
          const isOther = other.includes(a.slug);
          return (
            <button
              key={a.slug}
              type="button"
              onClick={() => onToggle(a.slug)}
              className={`text-left rounded-xl border-2 px-2.5 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)] ${
                isSel
                  ? "border-[var(--panini-red)] bg-[var(--panini-red)]/5"
                  : "border-[var(--ink)]/20 bg-white hover:bg-[var(--panini-yellow-soft)]/40"
              }`}
              aria-pressed={isSel}
              data-testid={`${testidPrefix}-${a.slug}`}
              title={isOther ? `Currently in "${title === "Completed" ? "In Progress" : "Completed"}" — click to move here.` : ""}
            >
              <div className="flex items-center gap-2.5">
                <AlbumCard slug={a.slug} size="sm" showTitle={false} />
                <div className="flex-1 min-w-0">
                  <div className={`text-xs leading-tight ${isSel ? "font-bold" : "font-medium"}`}>
                    {a.short_title || a.label}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-[var(--smoke)] leading-tight">
                    {a.host_display_name} · {a.year}
                  </div>
                </div>
                {isSel && (
                  <span className="text-[var(--panini-red)] font-bold" aria-hidden="true">✓</span>
                )}
                {!isSel && isOther && (
                  <span className="text-[9px] uppercase tracking-widest text-[var(--smoke)]">Other</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="grid md:grid-cols-[240px_1fr] gap-6">
      <div>
        <h3 className="font-display text-lg font-bold text-[var(--ink)]">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-[var(--smoke)]">{subtitle}</p>}
      </div>
      <div>{children}</div>
    </section>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">{label}</div>
      <input {...rest} data-testid={testid} className="gm-field" />
    </label>
  );
}

function SelectField({ label, options, testid, ...rest }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">{label}</div>
      <select {...rest} data-testid={testid} className="gm-field">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Textarea({ label, testid, ...rest }) {
  return (
    <label className="block mt-5">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">{label}</div>
      <textarea {...rest} data-testid={testid} className="gm-field resize-y" />
    </label>
  );
}

function SocialField({ label, icon, testid, ...rest }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">{label}</div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]">{icon}</span>
        <input {...rest} data-testid={testid} className="gm-field pl-9" />
      </div>
    </label>
  );
}

function UsernameField({ value, onChange, availability, testid }) {
  const meta = {
    idle:      { color: "", icon: null },
    checking:  { color: "text-[var(--smoke)]", icon: <Loader2 size={14} className="animate-spin" /> },
    invalid:   { color: "text-[var(--panini-red)]", icon: <XCircle size={14} /> },
    taken:     { color: "text-[var(--panini-red)]", icon: <XCircle size={14} /> },
    available: { color: "text-[color:var(--field-green)]", icon: <CheckCircle2 size={14} /> },
  }[availability?.state || "idle"];
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">Username</div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] font-mono text-sm">@</span>
        <input value={value} onChange={onChange} data-testid={testid}
                className="gm-field pl-8 pr-11" maxLength={32} />
        {meta.icon && (
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${meta.color}`} data-testid={`${testid}-status`}>
            {meta.icon}
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] flex justify-between">
        <span className={meta.color} data-testid={`${testid}-message`}>
          {availability?.message || "Your handle across the registry."}
        </span>
        <span className="text-[var(--smoke)]">{(value || "").length}/32</span>
      </div>
    </label>
  );
}



// ============================================================================
// Phase 4 — Privacy toggles
// ============================================================================
const PRIVACY_ROWS = [
  { key: "profile_public",     label: "Public profile page",   description: "Turn this on to publish /c/your-handle." },
  { key: "show_profile_photo", label: "Show avatar",           description: "Your avatar appears on the public profile." },
  { key: "show_bio",           label: "Show bio",              description: "Show the bio you wrote above." },
  { key: "show_country",       label: "Show country",          description: "Show the country from your profile." },
  { key: "show_social_links",  label: "Show social links",     description: "Show your Instagram, TikTok, X, etc." },
  { key: "show_certificates",  label: "Show certificates",     description: "Show up to 24 of your claimed certificates." },
  { key: "show_achievements",  label: "Show achievements",     description: "Show badges you've earned." },
  { key: "show_stats",         label: "Show stats",            description: "Show certificate counts and years collecting." },
];

function PrivacySection({ collector, onSaved }) {
  const [flags, setFlags] = React.useState(() => Object.fromEntries(
    PRIVACY_ROWS.map(({ key }) => [key, Boolean(collector?.[key])])));
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    // NARROW-TRACKED BY DESIGN: reseed flags only when the specific privacy fields
    // change server-side. Depending on the whole `collector` object would rerun on
    // every context refresh and clobber the optimistic toggle set below in `toggle`.
    // Intentional; not a stale-closure bug.
    setFlags(Object.fromEntries(PRIVACY_ROWS.map(({ key }) => [key, Boolean(collector?.[key])])));
  }, [collector?.id, collector?.profile_public, collector?.show_profile_photo,
      collector?.show_bio, collector?.show_country, collector?.show_social_links,
      collector?.show_certificates, collector?.show_achievements, collector?.show_stats]);

  const toggle = async (key) => {
    if (busy) return;
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    setBusy(true);
    try {
      await api.patch("/me/profile/privacy", { [key]: next[key] });
      toast.success("Privacy updated.");
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setFlags(flags);
    } finally {
      setBusy(false);
    }
  };

  const isPublic = flags.profile_public;
  return (
    <Section title="Public profile"
             subtitle="Nothing is public until you flip Public profile page on. Individual details have their own switches.">
      <div className="space-y-3" data-testid="privacy-section">
        {isPublic && collector?.username && (
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-3 text-sm flex items-center justify-between gap-2"
                data-testid="privacy-live-link">
            <span>Your public URL:</span>
            <a href={`/collectors/${collector.username}`} target="_blank" rel="noreferrer"
                className="font-mono font-bold underline">/c/{collector.username}</a>
          </div>
        )}
        {PRIVACY_ROWS.map(({ key, label, description }) => {
          const disabled = key !== "profile_public" && !isPublic;
          return (
            <label key={key} data-testid={`privacy-${key}`}
                    className={`flex items-start gap-4 rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[3px_3px_0_var(--ink)] cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
              <input type="checkbox" checked={Boolean(flags[key])}
                      onChange={() => !disabled && toggle(key)}
                      disabled={disabled || busy}
                      className="mt-1 w-4 h-4"
                      data-testid={`privacy-${key}-input`} />
              <div className="flex-1">
                <div className="font-bold text-[var(--ink)]">{label}</div>
                <div className="text-xs text-[var(--smoke)] mt-1">{description}</div>
              </div>
            </label>
          );
        })}
      </div>
    </Section>
  );
}
